import React, { useState, useEffect } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Sidebar } from "../../components/sidebar";
import { Wrapper } from "../../components/wrapper";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import SignalWifiOffIcon from "@mui/icons-material/SignalWifiOff";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SendIcon from "@mui/icons-material/Send";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import HistoryIcon from "@mui/icons-material/History";
import LogoutIcon from "@mui/icons-material/Logout";
import RefreshIcon from "@mui/icons-material/Refresh";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import SearchIcon from "@mui/icons-material/Search";
import PhoneIcon from "@mui/icons-material/Phone";
import InfoIcon from "@mui/icons-material/Info";
import {
  getWhatsAppStatus,
  disconnectWhatsApp,
  reconnectWhatsApp,
  sendByLicense,
  sendWhatsAppMessage,
} from "../../services/whatsapp";
import axios from "axios";
import config from "../../config/api.config";

export const WhatsApp = () => {
  const [status, setStatus] = useState("disconnected");
  const [qrCode, setQrCode] = useState(null);
  const [polling, setPolling] = useState(false);
  const [license, setLicense] = useState("");
  const [phone, setPhone] = useState("");
  const [customMessage, setCustomMessage] = useState("");
  const [carData, setCarData] = useState(null);
  const [sending, setSending] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [searchError, setSearchError] = useState(null);

  // On mount: check WhatsApp status via REST
  useEffect(() => {
    getWhatsAppStatus()
      .then((data) => {
        setStatus(data.status);
        if (data.qr) setQrCode(data.qr);
        // If not disconnected, start polling to track changes
        if (data.status !== "disconnected") {
          setPolling(true);
        }
      })
      .catch(() => {});
  }, []);

  // Polling: check status every 3s while active
  useEffect(() => {
    if (!polling) return;
    const interval = setInterval(async () => {
      try {
        const data = await getWhatsAppStatus();
        setStatus(data.status);
        if (data.qr) {
          setQrCode(data.qr);
        } else {
          setQrCode(null);
        }
        // Stop polling once fully connected or disconnected
        if (data.status === "connected" || data.status === "disconnected") {
          setPolling(false);
        }
        // Keep polling while "qr" or "connecting"
      } catch {
        // Server unreachable, stop polling
        setPolling(false);
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [polling]);

  const showFeedback = (message, type = "success") => {
    setFeedback({ message, type });
    setTimeout(() => setFeedback(null), 4000);
  };

  const handleSearch = async () => {
    if (!license.trim()) return;
    setSearchError(null);
    try {
      const { data } = await axios.post(
        `${config.apiUrl}/car/license`,
        { license: license.trim().toUpperCase() }
      );
      setCarData(data);
      if (data.phone) {
        setPhone(data.phone);
      }
    } catch (err) {
      setCarData(null);
      setSearchError("No se encontró un vehículo con esa patente");
    }
  };

  const handleSendPDF = async () => {
    if (!phone || !license) return;
    setSending(true);
    try {
      await sendByLicense(
        license.trim(),
        phone,
        carData?.current_km_used || carData?.kms,
        "pdf"
      );
      showFeedback("PDF enviado correctamente");
    } catch (err) {
      showFeedback(err.response?.data?.error || "Error al enviar PDF", "error");
    }
    setSending(false);
  };

  const handleSendHistory = async () => {
    if (!phone || !license) return;
    setSending(true);
    try {
      await sendByLicense(
        license.trim(),
        phone,
        carData?.current_km_used || carData?.kms,
        "text"
      );
      showFeedback("Historial enviado correctamente");
    } catch (err) {
      showFeedback(
        err.response?.data?.error || "Error al enviar historial",
        "error"
      );
    }
    setSending(false);
  };

  const handleSendCustom = async () => {
    if (!phone || !customMessage.trim()) return;
    setSending(true);
    try {
      await sendWhatsAppMessage(phone, customMessage);
      showFeedback("Mensaje enviado correctamente");
      setCustomMessage("");
    } catch (err) {
      showFeedback(
        err.response?.data?.error || "Error al enviar mensaje",
        "error"
      );
    }
    setSending(false);
  };

  const handleDisconnect = async () => {
    try {
      await disconnectWhatsApp();
      setPolling(false);
      setQrCode(null);
      setStatus("disconnected");
      showFeedback("WhatsApp desconectado");
    } catch (err) {
      showFeedback("Error al desconectar", "error");
    }
  };

  const handleReconnect = async () => {
    try {
      setStatus("connecting");
      await reconnectWhatsApp();
      // Start polling to receive QR and status updates
      setPolling(true);
    } catch (err) {
      setStatus("disconnected");
      showFeedback("Error al reconectar", "error");
    }
  };

  const StatusBadge = () => {
    const statusConfig = {
      connected: {
        icon: CheckCircleIcon,
        text: "Conectado",
        wrapper: "bg-green-500/10 border-green-500/30",
        textClass: "text-green-400",
      },
      qr: {
        icon: QrCode2Icon,
        text: "Escanear QR",
        wrapper: "bg-yellow-500/10 border-yellow-500/30",
        textClass: "text-yellow-400",
      },
      connecting: {
        icon: RefreshIcon,
        text: "Conectando...",
        wrapper: "bg-blue-500/10 border-blue-500/30",
        textClass: "text-blue-400",
      },
      disconnected: {
        icon: SignalWifiOffIcon,
        text: "Desconectado",
        wrapper: "bg-red-500/10 border-red-500/30",
        textClass: "text-red-400",
      },
    };
    const cfg = statusConfig[status] || statusConfig.disconnected;
    const Icon = cfg.icon;

    return (
      <div
        className={`flex items-center gap-2 px-4 py-2 rounded-full border ${cfg.wrapper}`}
      >
        <Icon className={cfg.textClass} sx={{ fontSize: 18 }} />
        <span className={`${cfg.textClass} text-sm font-medium`}>
          {cfg.text}
        </span>
      </div>
    );
  };

  return (
    <div className="flex w-full min-h-screen bg-sidebar-bg">
      <Sidebar />
      <div className="flex-1 w-full p-4">
        <Wrapper>
          <div className="w-full max-w-6xl mx-auto px-4 lg:px-6 py-8">
            {/* Header */}
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-500/10 rounded-lg">
                  <WhatsAppIcon
                    className="text-green-400"
                    sx={{ fontSize: 28 }}
                  />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-white">WhatsApp</h1>
                  <p className="text-gray-400 text-sm mt-1">
                    Envía pautas de mantención a tus clientes
                  </p>
                </div>
              </div>
              <StatusBadge />
            </div>

            {/* Feedback toast */}
            {feedback && (
              <div
                className={`mb-6 p-4 rounded-lg border ${
                  feedback.type === "error"
                    ? "bg-red-500/10 border-red-500/30 text-red-400"
                    : "bg-green-500/10 border-green-500/30 text-green-400"
                }`}
              >
                {feedback.message}
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* QR / Connection Panel */}
              <div className="bg-sidebar-bg rounded-xl border border-sidebar-accent p-6">
                <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                  <QrCode2Icon className="text-green-400" />
                  Conexión WhatsApp
                </h2>

                {status === "connected" ? (
                  <div className="text-center py-8">
                    <div className="w-20 h-20 mx-auto mb-4 bg-green-500/10 rounded-full flex items-center justify-center">
                      <CheckCircleIcon
                        className="text-green-400"
                        sx={{ fontSize: 48 }}
                      />
                    </div>
                    <p className="text-green-400 text-lg font-medium mb-2">
                      WhatsApp Conectado
                    </p>
                    <p className="text-gray-500 text-sm mb-6">
                      Listo para enviar mensajes y documentos
                    </p>
                    <div className="flex gap-3 justify-center">
                      <button
                        onClick={handleReconnect}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition-colors flex items-center gap-2"
                      >
                        <RefreshIcon sx={{ fontSize: 16 }} />
                        Reconectar
                      </button>
                      <button
                        onClick={handleDisconnect}
                        className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm rounded-lg transition-colors flex items-center gap-2"
                      >
                        <LogoutIcon sx={{ fontSize: 16 }} />
                        Desconectar
                      </button>
                    </div>
                  </div>
                ) : status === "qr" && qrCode ? (
                  <div className="text-center py-4">
                    <p className="text-gray-400 text-sm mb-4">
                      Escanea este código QR con WhatsApp
                    </p>
                    <div className="bg-white rounded-xl p-4 inline-block mb-4">
                      <QRCodeSVG value={qrCode} size={256} level="M" />
                    </div>
                    <p className="text-gray-500 text-xs mb-4">
                      WhatsApp {">"} Dispositivos vinculados {">"} Vincular dispositivo
                    </p>
                    <button
                      onClick={handleDisconnect}
                      className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm rounded-lg transition-colors flex items-center gap-2 mx-auto"
                    >
                      <LogoutIcon sx={{ fontSize: 16 }} />
                      Cancelar
                    </button>
                  </div>
                ) : status === "connecting" ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 mx-auto mb-4 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin" />
                    <p className="text-blue-400 font-medium">Conectando...</p>
                    <p className="text-gray-500 text-sm mt-1">
                      Estableciendo conexión con WhatsApp
                    </p>
                    <button
                      onClick={handleDisconnect}
                      className="mt-4 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm rounded-lg transition-colors flex items-center gap-2 mx-auto"
                    >
                      <LogoutIcon sx={{ fontSize: 16 }} />
                      Cancelar
                    </button>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="w-20 h-20 mx-auto mb-4 bg-red-500/10 rounded-full flex items-center justify-center">
                      <SignalWifiOffIcon
                        className="text-red-400"
                        sx={{ fontSize: 40 }}
                      />
                    </div>
                    <p className="text-red-400 font-medium mb-2">
                      Desconectado
                    </p>
                    <p className="text-gray-500 text-sm mb-4">
                      Conéctate para enviar mensajes
                    </p>
                    <button
                      onClick={handleReconnect}
                      className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors flex items-center gap-2 mx-auto"
                    >
                      <WhatsAppIcon sx={{ fontSize: 18 }} />
                      Conectar WhatsApp
                    </button>
                  </div>
                )}

                {/* Instructions */}
                <div className="mt-6 p-4 bg-black/30 rounded-lg border border-sidebar-accent">
                  <div className="flex items-start gap-2">
                    <InfoIcon
                      className="text-blue-400 flex-shrink-0 mt-0.5"
                      sx={{ fontSize: 16 }}
                    />
                    <div className="text-xs text-gray-500 space-y-1">
                      <p>1. Abre WhatsApp en tu teléfono</p>
                      <p>2. Ve a Dispositivos vinculados</p>
                      <p>3. Escanea el código QR que aparece aquí</p>
                      <p>4. La sesión se mantiene al reiniciar el servidor</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Send Panel */}
              <div className="bg-sidebar-bg rounded-xl border border-sidebar-accent p-6">
                <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                  <SendIcon className="text-green-400" />
                  Envío Rápido
                </h2>

                {/* Search by license */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                      Buscar por Patente
                    </label>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <DirectionsCarIcon
                          className="absolute left-3 top-3 text-gray-500"
                          sx={{ fontSize: 20 }}
                        />
                        <input
                          type="text"
                          value={license}
                          onChange={(e) =>
                            setLicense(e.target.value.toUpperCase())
                          }
                          onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                          placeholder="ABCD12"
                          maxLength={6}
                          className="w-full pl-10 pr-4 py-2.5 bg-black border border-sidebar-accent rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                        />
                      </div>
                      <button
                        onClick={handleSearch}
                        className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                      >
                        <SearchIcon sx={{ fontSize: 20 }} />
                      </button>
                    </div>
                    {searchError && (
                      <p className="text-red-400 text-xs mt-1">{searchError}</p>
                    )}
                  </div>

                  {/* Car data card */}
                  {carData && (
                    <div className="p-3 bg-green-500/5 border border-green-500/20 rounded-lg">
                      <p className="text-green-400 text-sm font-medium">
                        {carData.brand} {carData.model} {carData.year || ""}
                      </p>
                      <p className="text-gray-400 text-xs">
                        Cliente: {carData.customer_name} | KM:{" "}
                        {Number(
                          carData.current_km_used || carData.kms || 0
                        ).toLocaleString("es-CL")}
                      </p>
                      {carData.maintenance_summary && (
                        <div className="flex gap-3 mt-1">
                          <span className="text-xs text-red-400">
                            {carData.maintenance_summary.required} requeridos
                          </span>
                          <span className="text-xs text-yellow-400">
                            {carData.maintenance_summary.upcoming} próximos
                          </span>
                          <span className="text-xs text-green-400">
                            {carData.maintenance_summary.upToDate} al día
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Phone number */}
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                      Teléfono del cliente
                    </label>
                    <div className="relative">
                      <PhoneIcon
                        className="absolute left-3 top-3 text-gray-500"
                        sx={{ fontSize: 20 }}
                      />
                      <input
                        type="text"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="+56 9 1234 5678"
                        className="w-full pl-10 pr-4 py-2.5 bg-black border border-sidebar-accent rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                      />
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={handleSendPDF}
                      disabled={
                        status !== "connected" || !phone || !license || sending
                      }
                      className="flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors"
                    >
                      <PictureAsPdfIcon sx={{ fontSize: 18 }} />
                      {sending ? "Enviando..." : "Enviar PDF"}
                    </button>
                    <button
                      onClick={handleSendHistory}
                      disabled={
                        status !== "connected" || !phone || !license || sending
                      }
                      className="flex items-center justify-center gap-2 px-4 py-3 bg-green-600 hover:bg-green-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors"
                    >
                      <HistoryIcon sx={{ fontSize: 18 }} />
                      {sending ? "Enviando..." : "Enviar Historial"}
                    </button>
                  </div>

                  {/* Custom message */}
                  <div className="pt-2 border-t border-sidebar-accent">
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                      Mensaje personalizado
                    </label>
                    <textarea
                      value={customMessage}
                      onChange={(e) => setCustomMessage(e.target.value)}
                      placeholder="Escribe un mensaje..."
                      rows={3}
                      className="w-full px-4 py-2.5 bg-black border border-sidebar-accent rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none text-sm"
                    />
                    <button
                      onClick={handleSendCustom}
                      disabled={
                        status !== "connected" ||
                        !phone ||
                        !customMessage.trim() ||
                        sending
                      }
                      className="mt-2 w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-green-600 hover:bg-green-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors"
                    >
                      <SendIcon sx={{ fontSize: 16 }} />
                      {sending ? "Enviando..." : "Enviar Mensaje"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Wrapper>
      </div>
    </div>
  );
};

export default WhatsApp;
